import { useState, useEffect } from 'react';
import { C } from '../styles/colours.js';
import { LANGUAGES, T } from '../translations.js';

const INSTALL_TIP = {
  en: {
    banner: 'Get TabliQ on your home screen — like a real app!',
    how: 'Show me how',
    guideTitle: '📲 Install TabliQ',
    steps: [
      { icon: '📤', text: 'Tap the Share button at the bottom of Safari (the square with the arrow pointing up)' },
      { icon: '📜', text: 'Scroll down the list' },
      { icon: '➕', text: 'Tap "Add to Home Screen", then "Add"' },
    ],
    done: 'The TQ icon will appear with your apps — tap it and TabliQ opens full screen!',
    gotIt: 'Got it!',
  },
  fr: {
    banner: "Mets TabliQ sur ton écran d'accueil — comme une vraie app !",
    how: 'Montre-moi comment',
    guideTitle: '📲 Installer TabliQ',
    steps: [
      { icon: '📤', text: 'Touche le bouton Partager en bas de Safari (le carré avec la flèche vers le haut)' },
      { icon: '📜', text: 'Fais défiler la liste vers le bas' },
      { icon: '➕', text: 'Touche "Sur l\'écran d\'accueil", puis "Ajouter"' },
    ],
    done: "L'icône TQ apparaîtra avec tes apps — touche-la et TabliQ s'ouvre en plein écran !",
    gotIt: 'Compris !',
  },
  ar: {
    banner: 'ضع TabliQ على شاشتك الرئيسية — مثل تطبيق حقيقي!',
    how: 'أرني كيف',
    guideTitle: '📲 تثبيت TabliQ',
    steps: [
      { icon: '📤', text: 'اضغط زر المشاركة في أسفل Safari (المربع مع السهم لأعلى)' },
      { icon: '📜', text: 'مرر القائمة للأسفل' },
      { icon: '➕', text: 'اضغط "إضافة إلى الشاشة الرئيسية" ثم "إضافة"' },
    ],
    done: 'ستظهر أيقونة TQ مع تطبيقاتك — اضغط عليها ويفتح TabliQ بملء الشاشة!',
    gotIt: 'فهمت!',
  },
  es: {
    banner: '¡Pon TabliQ en tu pantalla de inicio — como una app de verdad!',
    how: 'Muéstrame cómo',
    guideTitle: '📲 Instalar TabliQ',
    steps: [
      { icon: '📤', text: 'Toca el botón Compartir abajo en Safari (el cuadrado con la flecha hacia arriba)' },
      { icon: '📜', text: 'Desliza la lista hacia abajo' },
      { icon: '➕', text: 'Toca "Añadir a pantalla de inicio", luego "Añadir"' },
    ],
    done: '¡El icono TQ aparecerá con tus apps — tócalo y TabliQ se abre a pantalla completa!',
    gotIt: '¡Entendido!',
  },
};

const SOLO_LABEL = {
  en: '🎯 Solo Play', fr: '🎯 Jouer en Solo', ar: '🎯 العب فردي', es: '🎯 Jugar Solo',
};
const LOGIN_LABEL = {
  en: 'Log In', fr: 'Connexion', ar: 'تسجيل الدخول', es: 'Iniciar sesión',
};
const BIGSCREEN_LABEL = {
  en: { btn: '📺 Big screen (TV)', hint: 'At the TV? Connect it to a live game' },
  fr: { btn: '📺 Grand écran (TV)', hint: 'Devant la TV ? Connecte-la à une partie en cours' },
  ar: { btn: '📺 الشاشة الكبيرة (TV)', hint: 'أمام التلفزيون؟ اربطه بلعبة جارية' },
  es: { btn: '📺 Pantalla grande (TV)', hint: '¿Frente a la TV? Conéctala a una partida en curso' },
};
const MENU_LABELS = {
  en: { profile: 'Profile', logout: 'Log out' },
  fr: { profile: 'Profil', logout: 'Déconnexion' },
  ar: { profile: 'الملف الشخصي', logout: 'تسجيل الخروج' },
  es: { profile: 'Perfil', logout: 'Cerrar sesión' },
};
const LEGAL_LABEL = {
  en: { terms: 'Terms', privacy: 'Privacy', refunds: 'Refunds' },
  fr: { terms: 'Conditions', privacy: 'Confidentialité', refunds: 'Remboursements' },
  ar: { terms: 'الشروط', privacy: 'الخصوصية', refunds: 'الاسترداد' },
  es: { terms: 'Términos', privacy: 'Privacidad', refunds: 'Reembolsos' },
};

const HTP_EXTRA = {
  en: {
    modesTitle: '🎲 More ways to play',
    soloTitle: '🎯 Solo Play',
    soloDesc: 'No group around? Play on your own — climb 15 levels of multiple-choice questions against the clock and chase the global leaderboard.',
    bigTitle: '📺 Big screen (TV)',
    bigDesc: 'Cast the game to a TV. The big screen shows the question, who buzzed first, the live scores and the winner — players still join and buzz from their phones.',
    packsTitle: '📚 Ready-made packs',
    packsDesc: 'No time to write questions? Open the Latest tab, pick a pack or grab questions from the categories, and start in seconds.',
  },
  fr: {
    modesTitle: '🎲 Autres façons de jouer',
    soloTitle: '🎯 Jeu Solo',
    soloDesc: "Pas de groupe ? Joue en solo — grimpe 15 niveaux de questions à choix multiple contre la montre et vise le classement mondial.",
    bigTitle: '📺 Grand écran (TV)',
    bigDesc: "Diffuse le jeu sur une TV. Le grand écran affiche la question, qui a buzzé en premier, les scores en direct et le gagnant — les joueurs rejoignent et buzzent depuis leur téléphone.",
    packsTitle: '📚 Packs prêts à jouer',
    packsDesc: "Pas le temps d'écrire des questions ? Ouvre l'onglet Nouveautés, choisis un pack ou prends des questions dans les catégories, et démarre en quelques secondes.",
  },
  ar: {
    modesTitle: '🎲 طرق أخرى للعب',
    soloTitle: '🎯 اللعب الفردي',
    soloDesc: 'لا يوجد مجموعة؟ العب بمفردك — اصعد 15 مستوى من أسئلة الاختيار من متعدد ضد الوقت وتنافس على لوحة المتصدرين العالمية.',
    bigTitle: '📺 الشاشة الكبيرة (TV)',
    bigDesc: 'اعرض اللعبة على التلفزيون. تعرض الشاشة الكبيرة السؤال، ومن ضغط أولاً، والنتائج المباشرة، والفائز — ينضم اللاعبون ويضغطون من هواتفهم.',
    packsTitle: '📚 حزم جاهزة',
    packsDesc: 'لا وقت لكتابة الأسئلة؟ افتح تبويب الأحدث، اختر حزمة أو خذ أسئلة من الفئات، وابدأ في ثوانٍ.',
  },
  es: {
    modesTitle: '🎲 Más formas de jugar',
    soloTitle: '🎯 Juego Solo',
    soloDesc: '¿Sin grupo? Juega solo — sube 15 niveles de preguntas de opción múltiple contra el reloj y compite por la clasificación mundial.',
    bigTitle: '📺 Pantalla grande (TV)',
    bigDesc: 'Proyecta el juego en una TV. La pantalla grande muestra la pregunta, quién pulsó primero, los puntajes en vivo y el ganador — los jugadores se unen y pulsan desde su teléfono.',
    packsTitle: '📚 Packs listos',
    packsDesc: '¿Sin tiempo para escribir preguntas? Abre la pestaña Lo último, elige un pack o toma preguntas de las categorías, y empieza en segundos.',
  },
};

// The real iOS Share icon (square with arrow pointing up) — drawn to match Safari's
function IOSShareIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#4da3ff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 8H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2h-2" />
      <path d="M12 14V3" />
      <path d="M8.5 6.5L12 3l3.5 3.5" />
    </svg>
  );
}

function Welcome({ onHost, onPlayer, onSolo, onLogin, onLogout, onProfile, user, lang, setLang }) {
  const [showHow, setShowHow] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showInstallTip, setShowInstallTip] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  useEffect(() => {
    try {
      const ua = window.navigator.userAgent || '';
      const isIPhone = /iphone/i.test(ua);
      const isStandalone = window.navigator.standalone === true ||
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
      const dismissed = window.localStorage.getItem('tq_install_tip_dismissed') === '1';
      if (isIPhone && !isStandalone && !dismissed) setShowInstallTip(true);
    } catch (e) { /* never block the app for a tip */ }
  }, []);

  const dismissInstallTip = () => {
    setShowInstallTip(false);
    try { window.localStorage.setItem('tq_install_tip_dismissed', '1'); } catch (e) {}
  };

  const t = T[lang] || T.en;
  const isRTL = lang === 'ar';
  const soloLabel = SOLO_LABEL[lang] || SOLO_LABEL.en;
  const loginLabel = LOGIN_LABEL[lang] || LOGIN_LABEL.en;
  const bigBtn = BIGSCREEN_LABEL[lang] || BIGSCREEN_LABEL.en;
  const menu = MENU_LABELS[lang] || MENU_LABELS.en;
  const legal = LEGAL_LABEL[lang] || LEGAL_LABEL.en;
  const installTip = INSTALL_TIP[lang] || INSTALL_TIP.en;
  const he = HTP_EXTRA[lang] || HTP_EXTRA.en;
  const userName = user?.user_metadata?.name || user?.email?.split('@')[0] || '';
  const initial = (userName || 'U').charAt(0).toUpperCase();

  const welcomeBackground = 'radial-gradient(ellipse at 50% 25%, rgba(232, 82, 106, 0.10) 0%, rgba(7,7,26,0) 55%), #07071A';

  const menuItemStyle = {
    width: '100%',
    textAlign: isRTL ? 'right' : 'left',
    background: 'transparent',
    border: 'none',
    color: C.white,
    padding: '9px 12px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const legalLinkStyle = {
    color: C.textMuted,
    textDecoration: 'none',
    fontSize: 12,
    fontWeight: 600,
  };

  if (showHow) {
    const steps = [
      { step: '1', color: C.gold, icon: '🎙️', title: t.htpStep1Title, desc: t.htpStep1Desc },
      { step: '2', color: C.green, icon: '📱', title: t.htpStep2Title, desc: t.htpStep2Desc },
      { step: '3', color: C.red, icon: '🔒', title: t.htpStep3Title, desc: t.htpStep3Desc },
      { step: '4', color: C.purple, icon: '🔓', title: t.htpStep4Title, desc: t.htpStep4Desc },
      { step: '5', color: C.blue, icon: '🔔', title: t.htpStep5Title, desc: t.htpStep5Desc },
      { step: '6', color: C.gold, icon: '🏆', title: t.htpStep6Title, desc: t.htpStep6Desc },
    ];
    const tips = [t.htpTip1, t.htpTip2, t.htpTip3, t.htpTip4];

    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <button onClick={() => setShowHow(false)} style={{ background: C.card, border: 'none', color: C.white, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>{t.back}</button>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, color: C.white }}>TQ</div>
        </div>

        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 6, color: C.white }}>{t.howToPlayTitle}</div>
          <div style={{ fontSize: 16, color: C.white, lineHeight: 1.6, opacity: 0.85 }}>{t.howToPlayIntro}</div>
        </div>

        {steps.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <div style={{ flexShrink: 0 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: `${item.color}25`, border: `1px solid ${item.color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{item.icon}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ background: item.color, color: '#000', borderRadius: 20, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, flexShrink: 0 }}>{item.step}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.white }}>{item.title}</div>
              </div>
              <div style={{ fontSize: 15, color: C.white, lineHeight: 1.7, opacity: 0.9 }}>{item.desc}</div>
            </div>
          </div>
        ))}

        <div style={{ background: `${C.gold}15`, border: `1px solid ${C.gold}35`, borderRadius: 16, padding: '16px', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.gold, marginBottom: 10 }}>{t.htpTipsTitle}</div>
          {tips.map((tip, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: i < tips.length - 1 ? 10 : 0 }}>
              <div style={{ color: C.gold, fontWeight: 700, flexShrink: 0 }}>{isRTL ? '←' : '→'}</div>
              <div style={{ fontSize: 15, color: C.white, lineHeight: 1.6, opacity: 0.9 }}>{tip}</div>
            </div>
          ))}
        </div>

        {/* More ways to play */}
        <div style={{ fontSize: 18, fontWeight: 900, color: C.white, marginBottom: 14 }}>{he.modesTitle}</div>
        {[
          { icon: '🎯', color: C.purple, title: he.soloTitle, desc: he.soloDesc },
          { icon: '📺', color: C.blue, title: he.bigTitle, desc: he.bigDesc },
          { icon: '📚', color: C.green, title: he.packsTitle, desc: he.packsDesc },
        ].map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 14, background: C.card, border: `1px solid ${m.color}30`, borderRadius: 14, padding: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${m.color}25`, border: `1px solid ${m.color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{m.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>{m.title}</div>
              <div style={{ fontSize: 15, color: C.white, opacity: 0.9, lineHeight: 1.6 }}>{m.desc}</div>
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          <button onClick={onHost} style={{ width: '100%', background: C.gold, color: '#000', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>{t.imHost}</button>
          <button onClick={onPlayer} style={{ width: '100%', background: C.red, color: C.white, border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>{t.imPlayer}</button>
          <button onClick={onSolo} style={{ width: '100%', background: C.purple, color: C.white, border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>{soloLabel}</button>
        </div>
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ position: 'relative', background: welcomeBackground, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', gap: 16 }}>

      {/* Top-right auth control */}
      <div style={{ position: 'absolute', top: 16, [isRTL ? 'left' : 'right']: 16, zIndex: 20 }}>
        {user ? (
          <>
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 30, padding: isRTL ? '5px 5px 5px 12px' : '5px 12px 5px 5px', cursor: 'pointer' }}
            >
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, color: '#fff', flexShrink: 0 }}>{initial}</div>
              <span style={{ fontSize: 13, color: C.white, fontWeight: 600, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</span>
              <span style={{ fontSize: 9, color: C.textSub }}>▼</span>
            </button>

            {menuOpen && (
              <>
                <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }} />
                <div style={{ position: 'absolute', top: 46, [isRTL ? 'left' : 'right']: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 6, minWidth: 190, boxShadow: '0 12px 30px rgba(0,0,0,0.5)', zIndex: 20 }}>
                  <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
                    {user.email && <div style={{ fontSize: 11, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>}
                  </div>
                  <button onClick={() => { setMenuOpen(false); onProfile && onProfile(); }} style={menuItemStyle}>👤 {menu.profile}</button>
                  <button onClick={() => { setMenuOpen(false); onLogout && onLogout(); }} style={{ ...menuItemStyle, color: C.red }}>🚪 {menu.logout}</button>
                </div>
              </>
            )}
          </>
        ) : (
          <button onClick={onLogin} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.18)', color: C.white, borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {loginLabel}
          </button>
        )}
      </div>

      <div style={{ width: 100, height: 100, background: 'linear-gradient(145deg, #e8526a, #c0392b)', borderRadius: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 42, fontWeight: 900, color: C.white, boxShadow: '0 10px 40px rgba(232,82,106,0.3)' }}>TQ</div>

      <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: 2 }}>
        Tabli<span style={{ color: C.red }}>Q</span>
      </div>

      <div style={{ fontSize: 12, letterSpacing: 4, color: C.textSub }}>{t.tagline}</div>

      <div style={{ display: 'flex', gap: 8, marginTop: 4, background: C.card, borderRadius: 30, padding: '6px 10px', border: `1px solid ${C.border}` }}>
        {LANGUAGES.map(l => (
          <button
            key={l.code}
            onClick={() => setLang(l.code)}
            style={{
              background: lang === l.code ? 'rgba(255,209,102,0.2)' : 'transparent',
              border: lang === l.code ? `1.5px solid ${C.gold}` : '1.5px solid transparent',
              borderRadius: 20,
              padding: '4px 8px',
              fontSize: 24,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            title={l.label}
          >
            {l.flag}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10, width: '100%', maxWidth: 280 }}>
        <button onClick={onHost} style={{ width: '100%', background: C.gold, color: '#000', border: 'none', borderRadius: 14, padding: '16px', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
          {t.imHost}
        </button>
        <button onClick={onPlayer} style={{ width: '100%', background: C.red, color: C.white, border: 'none', borderRadius: 14, padding: '16px', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
          {t.imPlayer}
        </button>
        <button onClick={onSolo} style={{ width: '100%', background: C.purple, color: C.white, border: 'none', borderRadius: 14, padding: '16px', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: `0 4px 20px ${C.purple}40` }}>
          {soloLabel}
        </button>
        <button onClick={() => setShowHow(true)} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', color: C.white, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 14, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {t.howToPlay}
        </button>
        <button onClick={() => { window.location.href = window.location.origin + '/?tv='; }} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', color: C.white, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 14, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
          {bigBtn.btn}
          <div style={{ fontSize: 11, color: C.textSub, fontWeight: 500, marginTop: 3 }}>{bigBtn.hint}</div>
        </button>
      </div>

      {/* Legal footer */}
      <div style={{ marginTop: 14, textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginBottom: 6 }}>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={legalLinkStyle}>{legal.terms}</a>
          <span style={{ color: C.textMuted, fontSize: 12 }}>·</span>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={legalLinkStyle}>{legal.privacy}</a>
          <span style={{ color: C.textMuted, fontSize: 12 }}>·</span>
          <a href="/refunds.html" target="_blank" rel="noopener noreferrer" style={legalLinkStyle}>{legal.refunds}</a>
        </div>
        <div style={{ fontSize: 11, color: C.textMuted }}>© 2026 Tabliq</div>
      </div>

      {/* iOS install tip banner */}
      {showInstallTip && !showInstallGuide && (
        <div style={{ position: 'fixed', bottom: 14, left: '50%', transform: 'translateX(-50%)', width: 'calc(100% - 28px)', maxWidth: 440, background: C.card, border: `1px solid ${C.gold}50`, borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, zIndex: 50, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 13, color: C.white, flexShrink: 0 }}>TQ</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, color: C.white, lineHeight: 1.4, marginBottom: 8 }}>{installTip.banner}</div>
            <button onClick={() => setShowInstallGuide(true)} style={{ background: C.gold, color: '#000', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
              {installTip.how}
            </button>
          </div>
          <button onClick={dismissInstallTip} aria-label="Close" style={{ background: 'transparent', border: 'none', color: C.textSub, fontSize: 18, cursor: 'pointer', padding: 4, flexShrink: 0, alignSelf: 'flex-start' }}>✕</button>
        </div>
      )}

      {/* iOS install step-by-step guide */}
      {showInstallGuide && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(7,7,26,0.96)', zIndex: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 380 }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: C.white, marginBottom: 20, textAlign: 'center' }}>{installTip.guideTitle}</div>

            {installTip.steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 10 }}>
                <div style={{ background: C.gold, color: '#000', borderRadius: 20, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, flexShrink: 0 }}>{i + 1}</div>
                <div style={{ fontSize: 28, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  {i === 0 ? <IOSShareIcon size={30} /> : s.icon}
                </div>
                <div style={{ fontSize: 15, color: C.white, lineHeight: 1.5 }}>{s.text}</div>
              </div>
            ))}

            <div style={{ fontSize: 15, color: C.green, lineHeight: 1.5, textAlign: 'center', margin: '16px 0 20px', fontWeight: 700 }}>
              {installTip.done}
            </div>

            <button onClick={() => { setShowInstallGuide(false); dismissInstallTip(); }} style={{ width: '100%', background: C.gold, color: '#000', border: 'none', borderRadius: 14, padding: 16, fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
              {installTip.gotIt}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default Welcome;
